import { useEffect, useMemo, useState } from "react";
import dateFormat from "dateformat";
import { createUseStyles } from "react-jss";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { theme } from "$utils/theme/bataillog";
import { ShippingRecord } from "$types";
import { DEFAULT_DATE_FORMAT, toLocalDateString } from "$utils/utils";
import { getShippingRecordWishedDeliveryDate } from "$utils/typeConstants/ShippingRecord";
import InfoContainer from "$components/InfoContainer";
import Text from "$components/Text";
import BataillogDatePicker from "$components/BataillogDatePicker/BataillogDatePicker";

type Props = {
  classes: any;
  shippingRecord: ShippingRecord;
  updateShippingRecord: (shippingRecord: Partial<ShippingRecord>) => void;
  disabled?: boolean;
};

function ShippingDeliveryDatesBlock({
  shippingRecord,
  classes,
  updateShippingRecord,
  disabled = false,
}: Props) {
  const toDate = (v: string | Date | null | undefined): Date | null =>
    v ? (v instanceof Date ? v : new Date(v)) : null;

  const [wishedShippingDate, setWishedShippingDate] = useState<Date | null>(
    toDate(shippingRecord?.wishedShippingDate)
  );
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState<Date | null>(
    toDate(shippingRecord?.expectedDeliveryDate)
  );

  useEffect(() => {
    setWishedShippingDate(toDate(shippingRecord?.wishedShippingDate));
  }, [shippingRecord?.wishedShippingDate]);

  useEffect(() => {
    setExpectedDeliveryDate(toDate(shippingRecord?.expectedDeliveryDate));
  }, [shippingRecord?.expectedDeliveryDate]);

  const localClasses = useStyles();

  const wishedDeliveryDate = useMemo(
    () => getShippingRecordWishedDeliveryDate(shippingRecord),
    [shippingRecord]
  );

  const handleChangeWishedShippingDate = (date: Date) => {
    setWishedShippingDate(date);
    setTimeout(
      () =>
        updateShippingRecord({ wishedShippingDate: toLocalDateString(date) }),
      500
    );
  };

  const handleChangeExpectedDeliveryDate = (date: Date) => {
    setExpectedDeliveryDate(date);
    setTimeout(
      () =>
        updateShippingRecord({
          expectedDeliveryDate: toLocalDateString(date),
        }),
      500
    );
  };

  return (
    <InfoContainer
      className={classes.shippingDeliveryDatesPaper}
      leftHeader={
        <div className={classes.infoHeader}>
          <EventOutlinedIcon />
          <Text type="h3">Dates de livraison</Text>
        </div>
      }
    >
      <div className={localClasses.datesContainer}>
        <div>
          <Text type="text" className={classes.subTitle}>
            {"Date de livraison souhaitée : "}
          </Text>
          <Text type="important">
            {wishedDeliveryDate
              ? dateFormat(wishedDeliveryDate, DEFAULT_DATE_FORMAT)
              : ""}
          </Text>
        </div>
        {disabled ? (
          <div className={localClasses.dateField}>
            <Text type="text" className={classes.subTitle}>
              {"Date d'enlèvement souhaitée : "}
            </Text>
            <Text type="important">
              {shippingRecord?.wishedShippingDate
                ? dateFormat(
                    shippingRecord?.wishedShippingDate,
                    DEFAULT_DATE_FORMAT
                  )
                : ""}
            </Text>
          </div>
        ) : (
          <div className={localClasses.dateField}>
            <div className={classes.inputContainer}>
              <BataillogDatePicker
                label="Date d'enlèvement souhaitée :"
                selectedDate={wishedShippingDate}
                style={{ width: "100%" }}
                onDateChange={handleChangeWishedShippingDate}
              />
            </div>
          </div>
        )}
        {disabled ? (
          <div className={localClasses.dateField}>
            <Text type="text" className={classes.subTitle}>
              {"Date de livraison prévue : "}
            </Text>
            <Text type="important">
              {shippingRecord?.expectedDeliveryDate
                ? dateFormat(
                    shippingRecord?.expectedDeliveryDate,
                    DEFAULT_DATE_FORMAT
                  )
                : ""}
            </Text>
          </div>
        ) : (
          <div className={localClasses.dateField}>
            <div className={classes.inputContainer}>
              <BataillogDatePicker
                label="Date de livraison prévue :"
                selectedDate={expectedDeliveryDate}
                style={{ width: "100%" }}
                onDateChange={handleChangeExpectedDeliveryDate}
              />
            </div>
          </div>
        )}
      </div>
    </InfoContainer>
  );
}

export default ShippingDeliveryDatesBlock;

const useStyles = createUseStyles(() => ({
  datesContainer: {
    display: "flex",
    flexDirection: "column",
  },
  dateField: {
    marginTop: theme.spacing(2),
    // Match "Date de livraison souhaitée :" (Text + classes.subTitle)
    "& .MuiInputLabel-root": {
      fontFamily: theme.font.workSans,
      fontWeight: "bold",
      fontSize: "14px !important",
      color: "#000000 !important",
      // Keep same color when focused / shrunk (MUI otherwise turns gray/blue)
      "&.Mui-focused": {
        color: "#000000 !important",
      },
    },
  },
}));
